import { create } from 'zustand';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';

interface ActionResultData {
  inspectionId: string;
  findingId: string;
  severity: Severity;
  existingNotes?: string;
  existingResolutionPhotoUrl?: string;
  existingResolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  existingIsResolved?: boolean; // 기존 해결 상태
  existingResolvedAt?: string; // 기존 해결 날짜 (ISO 8601)
  measures?: string[]; // AI 개선대책 프리필용
  nextInspectionDate?: string; // 다음 점검일 (날짜 선택 max 제한용, YYYY-MM-DD)
  inspectionCreatedAt?: string; // 현재 점검 시작일 (날짜 선택 min 제한용, ISO 8601)
}

/**
 * 현장 생성 모달의 진입 출처.
 * - null: FAB/기본 (저장 후 현장 생성 흐름 종료)
 * - 'inspection': CreateInspectionModal '현장 추가' 버튼 (저장 후 CreateInspectionModal 자동 재오픈)
 */
export type CreateSiteSource = null | 'inspection';

interface ModalState {
  // 생성 모달 상태
  createOrganization: boolean;
  createSite: boolean;
  createSiteSource: CreateSiteSource;
  createInspection: boolean;
  createInspectionDefaultDate: string | null;
  createReport: boolean;
  createTbm: boolean;

  // 편집 모달 상태 (ID 포함)
  editSite: string | null;

  // 해결 사진 업로드 모달 (발견사항 ID 포함)
  resolutionPhoto: { inspectionId: string; findingId: string } | null;

  // 이전 발견사항 조치결과 입력 모달
  actionResult: ActionResultData | null;

  // 카메라 촬영 모달
  cameraCapture: boolean;

  // 조직 생성 모달
  openCreateOrganization: () => void;
  closeCreateOrganization: () => void;

  // 현장 생성 모달
  openCreateSite: () => void;
  /** CreateInspectionModal 내 '현장 추가' 전용 — 저장 후 자동으로 CreateInspectionModal 재오픈 */
  openCreateSiteForInspection: () => void;
  closeCreateSite: () => void;

  // 현장 편집 모달
  openEditSite: (siteId: string) => void;
  closeEditSite: () => void;

  // 점검 생성 모달
  openCreateInspection: (defaultDate?: string) => void;
  closeCreateInspection: () => void;

  // 보고서 생성 모달
  openCreateReport: () => void;
  closeCreateReport: () => void;

  // TBM 생성 모달
  openCreateTbm: () => void;
  closeCreateTbm: () => void;

  // 해결 사진 업로드 모달
  openResolutionPhoto: (inspectionId: string, findingId: string) => void;
  closeResolutionPhoto: () => void;

  // 이전 발견사항 조치결과 입력 모달
  openActionResult: (data: ActionResultData) => void;
  closeActionResult: () => void;

  // 카메라 촬영 모달
  openCameraCapture: () => void;
  closeCameraCapture: () => void;

  // 모든 모달 닫기
  closeAll: () => void;
}

export const useModalStore = create<ModalState>((set) => ({
  // 초기 상태
  createOrganization: false,
  createSite: false,
  createSiteSource: null,
  createInspection: false,
  createInspectionDefaultDate: null,
  createReport: false,
  createTbm: false,
  editSite: null,
  resolutionPhoto: null,
  actionResult: null,
  cameraCapture: false,

  // 조직 생성
  openCreateOrganization: () => set({ createOrganization: true }),
  closeCreateOrganization: () => set({ createOrganization: false }),

  // 현장 생성
  openCreateSite: () => set({ createSite: true, createSiteSource: null }),
  openCreateSiteForInspection: () => set({ createSite: true, createSiteSource: 'inspection' }),
  closeCreateSite: () => set({ createSite: false, createSiteSource: null }),

  // 현장 편집
  openEditSite: (siteId: string) => set({ editSite: siteId }),
  closeEditSite: () => set({ editSite: null }),

  // 점검 생성
  openCreateInspection: (defaultDate?: string) =>
    set({ createInspection: true, createInspectionDefaultDate: defaultDate ?? null }),
  closeCreateInspection: () => set({ createInspection: false, createInspectionDefaultDate: null }),

  // 보고서 생성
  openCreateReport: () => set({ createReport: true }),
  closeCreateReport: () => set({ createReport: false }),

  // TBM 생성
  openCreateTbm: () => set({ createTbm: true }),
  closeCreateTbm: () => set({ createTbm: false }),

  // 해결 사진 업로드
  openResolutionPhoto: (inspectionId: string, findingId: string) =>
    set({ resolutionPhoto: { inspectionId, findingId } }),
  closeResolutionPhoto: () => set({ resolutionPhoto: null }),

  // 이전 발견사항 조치결과 입력
  openActionResult: (data: ActionResultData) => set({ actionResult: data }),
  closeActionResult: () => set({ actionResult: null }),

  // 카메라 촬영
  openCameraCapture: () => set({ cameraCapture: true }),
  closeCameraCapture: () => set({ cameraCapture: false }),

  // 모든 모달 닫기
  closeAll: () =>
    set({
      createOrganization: false,
      createSite: false,
      createSiteSource: null,
      createInspection: false,
      createInspectionDefaultDate: null,
      createReport: false,
      createTbm: false,
      editSite: null,
      resolutionPhoto: null,
      actionResult: null,
      cameraCapture: false,
    }),
}));
